/**
 * Rules the canvas page applies around the primary toolbar (D31, D34).
 */

/**
 * Opening the Shapes tray never changes what is armed while a shape or a
 * connector already is; it arms the last shape only when nothing in the
 * Shapes family is armed.
 */
export function shapesTrayArmsLastShape({
  isPlacingShape,
  isPlacingConnector,
}: {
  isPlacingShape: boolean;
  isPlacingConnector: boolean;
}): boolean {
  return !isPlacingShape && !isPlacingConnector;
}

/**
 * The same rule at the page, where the flag lives: the legacy bar keeps
 * main's behaviour (P and the Shapes flyout always arm the last shape,
 * cancelling an armed connector); only the redrawn bar applies
 * `shapesTrayArmsLastShape`.
 */
export function shapesKeyArmsLastShape({
  toolbarRedraw,
  isPlacingShape,
  isPlacingConnector,
}: {
  toolbarRedraw: boolean;
  isPlacingShape: boolean;
  isPlacingConnector: boolean;
}): boolean {
  return (
    !toolbarRedraw ||
    shapesTrayArmsLastShape({ isPlacingShape, isPlacingConnector })
  );
}

/**
 * Pointer · Hand live on the primary bar's Cursor tile; the controls bar
 * carries the trigger wherever that bar does not mount: the legacy toolbar
 * (flag off) and a read-only canvas, where the primary bar never mounts.
 */
export function controlsBarCarriesCursor({
  toolbarRedraw,
  canEdit,
}: {
  toolbarRedraw: boolean;
  canEdit: boolean;
}): boolean {
  return !toolbarRedraw || !canEdit;
}

/**
 * An armed sticker or block stands down when another object placement arms
 * from a path that does not clear it — a key, an Insert row (ROUND2 §3).
 * The pen is deliberately not in this set: pen versus object is already
 * settled by the page's older rule, where an object placement stands the
 * pen down. Listing the pen here as well would fire both rules on the same
 * render and leave nothing armed.
 */
export function widgetYieldsToPlacement({
  isPlacingTextNote,
  isPlacingDoc,
  isPlacingTextLabel,
  isPlacingShape,
  isPlacingConnector,
  isPlacingSection,
}: {
  isPlacingTextNote: boolean;
  isPlacingDoc: boolean;
  isPlacingTextLabel: boolean;
  isPlacingShape: boolean;
  isPlacingConnector: boolean;
  isPlacingSection: boolean;
}): boolean {
  return (
    isPlacingTextNote ||
    isPlacingDoc ||
    isPlacingTextLabel ||
    isPlacingShape ||
    isPlacingConnector ||
    isPlacingSection
  );
}

/** The bar at full capability: ten 40px tiles, their gaps, the shell. */
export const CANVAS_PRIMARY_BAR_WIDTH_PX = 430;
/** The New Chat circle and the gap it keeps off the bar. */
export const CANVAS_NEW_CHAT_WIDTH_PX = 50;
export const CANVAS_NEW_CHAT_GAP_PX = 16;
/** The whole row: the bar, the gap, the disc. */
export const CANVAS_ROW_WIDTH_PX =
  CANVAS_PRIMARY_BAR_WIDTH_PX +
  CANVAS_NEW_CHAT_GAP_PX +
  CANVAS_NEW_CHAT_WIDTH_PX;
/**
 * The rail-side air: the row's left edge never goes inside this inset
 * (ROUND6, D53). The chat side keeps `CANVAS_CHAT_SIDE_AIR_PX`, already
 * folded into the band the clearance is taken from (D55).
 */
export const CANVAS_ROW_INSET_PX = 16;
/**
 * The air the redrawn toolbar keeps off the chat's (or the FAB's) painted
 * edge — --space-4, the toolbar family's air everywhere else (D55). It is
 * added to the chat's measured painted footprint to make the band.
 */
export const CANVAS_CHAT_SIDE_AIR_PX = 16;

/**
 * The redrawn toolbar's band: the wrapper less the chat's painted footprint
 * and the chat-side air; the whole wrapper when no chat stands on the
 * right. Before the footprint is measured, the legacy band stands in.
 */
export function redrawBandWidth({
  wrapperWidth,
  chatFootprint,
  legacyBand,
}: {
  wrapperWidth: number;
  chatFootprint: number;
  legacyBand: number;
}): number {
  if (!(wrapperWidth > 0) || !(chatFootprint > 0)) return legacyBand;
  return Math.max(0, wrapperWidth - chatFootprint - CANVAS_CHAT_SIDE_AIR_PX);
}

export interface RowPlacement {
  /** The row's left edge, in the wrapper's coordinates. */
  left: number;
  /** How far left of home the row stands: the overshoot, floored at the inset. */
  shift: number;
  /** The row cannot stand clear while a chat is open: the disc stands down. */
  discStandsDown: boolean;
  /** The bar alone cannot stand clear: shed to Cursor. */
  shed: boolean;
}

/**
 * Where the primary toolbar's row stands (ROUND6, D53). Its home is the
 * centre of the whole canvas wrapper. It moves left only when its right
 * edge at home would cross the chat's left edge (`wrapperWidth −
 * clearance`, the FAB's footprint while the chat is closed), and only by
 * the overshoot; never right of home; never inside the rail-side inset.
 * When even the full shift cannot keep the row clear, the rungs of D7
 * apply in their order — the disc stands down first while the chat is
 * open, then the bar sheds to Cursor — each judged by its own width, so a
 * shorter row gets another chance before the next rung. `rowWidth` is the
 * rendered row the shift is computed for; the rungs read the constants.
 */
export function rowPlacement({
  wrapperWidth,
  clearance,
  rowWidth,
  chatOpen,
  inset = CANVAS_ROW_INSET_PX,
}: {
  wrapperWidth: number;
  clearance: number;
  rowWidth: number;
  chatOpen: boolean;
  inset?: number;
}): RowPlacement {
  if (wrapperWidth <= 0) {
    return { left: 0, shift: 0, discStandsDown: false, shed: false };
  }
  const chatLeft = wrapperWidth - clearance;
  const fits = (width: number) => chatLeft - inset >= width;
  const discStandsDown = chatOpen && !fits(CANVAS_ROW_WIDTH_PX);
  const shed = !fits(CANVAS_PRIMARY_BAR_WIDTH_PX);
  const home = wrapperWidth / 2 - rowWidth / 2;
  const overshoot = Math.max(0, home + rowWidth - chatLeft);
  const left = Math.max(inset, home - overshoot);
  return { left, shift: home - left, discStandsDown, shed };
}
